import { API_BASE_URL, PERMISSIONS } from "@/config";
import { handleSessionExpiration } from "@/common/helpers/session-helper";
import { BaseService } from "@/services/base-service";

/** Default cache TTL: 20 hours (matching ITMarketplaceV2Website) */
const PERMISSIONS_CACHE_TTL_MS = 20 * 60 * 60 * 1000;

/**
 * UserPermissions — gates the app behind an authorize() call.
 *
 * authorize() calls /user-permissions via BaseService (sentry-fetch).
 * sentry-fetch handles the Midway redirect transparently. The Lambda
 * returns { username, allowedFeatures } after Midway authentication.
 *
 * This matches the ITMarketplaceV2Website pattern exactly.
 */
export class UserPermissions {
  public static current = new UserPermissions();

  private authenticatedUsername: string | null = null;
  private cachedPermissions: PERMISSIONS[] | null = null;
  private cacheExpiresAt = 0;
  private authorized = false;
  private reauthorizing = false;

  public get isAuth(): boolean {
    return !!this.authenticatedUsername;
  }

  public get username(): string | null {
    return this.authenticatedUsername;
  }

  public get hasAuthorized(): boolean {
    return this.authorized;
  }

  public get permissions(): PERMISSIONS[] {
    if (this.cachedPermissions && Date.now() < this.cacheExpiresAt) {
      return this.cachedPermissions;
    }
    return [];
  }

  public getPermissionsOrExpire(): PERMISSIONS[] {
    const perms = this.permissions;
    if (perms.length === 0 && this.authorized) {
      this.triggerSessionExpiration();
    }
    return perms;
  }

  public hasPermission(permission: PERMISSIONS): boolean {
    if (!this.isAuth) return false;
    if (!this.cachedPermissions || Date.now() >= this.cacheExpiresAt) return false;
    return this.cachedPermissions.includes(permission);
  }

  public async hasPermissionAsync(permission: PERMISSIONS): Promise<boolean> {
    return this.hasPermission(permission);
  }

  private readonly service = new BaseService(API_BASE_URL);

  /**
   * Calls /user-permissions via BaseService (sentry-fetch).
   * sentry-fetch handles Midway auth transparently. The Lambda returns
   * { username, allowedFeatures } from the X-FORWARDED-USER header.
   */
  public async authorize(): Promise<void> {
    const response = await this.service.fetch("/user-permissions");

    if (response.status !== 200) {
      throw new Error("An error occurred when authorizing the user.");
    }

    const json = await response.json();
    if (!json.username) {
      throw new Error("Midway authentication required");
    }

    this.authenticatedUsername = json.username;
    this.cachedPermissions = (json.allowedFeatures || []) as PERMISSIONS[];
    this.cacheExpiresAt = Date.now() + PERMISSIONS_CACHE_TTL_MS;
    this.authorized = true;
  }

  private triggerSessionExpiration(): void {
    if (this.reauthorizing) return;
    this.reauthorizing = true;
    handleSessionExpiration();
  }
}
